export interface KeyedAnimatedItem {
	key: string;
	row: number;
}

export interface KeyedAnimationDelta<T extends KeyedAnimatedItem> {
	sharedItems: T[];
	enteringItems: T[];
	exitingItems: T[];
	animationRows: number;
	hasDelta: boolean;
}

export type KeyedAnimationPhase = "stable" | "enter" | "exit";

export interface KeyedAnimationFrame<T extends KeyedAnimatedItem> {
	phase: KeyedAnimationPhase;
	stableItems: T[];
	animatedItems: T[];
}

export interface KeyedAnimationSequence<T extends KeyedAnimatedItem> {
	delta: KeyedAnimationDelta<T>;
	initialFrame: KeyedAnimationFrame<T>;
	followUpFrame: KeyedAnimationFrame<T> | null;
	settledItems: T[];
}

export function resolveAnimatedRows<T extends Pick<KeyedAnimatedItem, "row">>(
	items: T[],
): number {
	let maxRow = -1;
	for (const item of items) {
		if (item.row > maxRow) {
			maxRow = item.row;
		}
	}
	return maxRow + 1;
}

export function resolveKeyedAnimationDelta<
	T extends KeyedAnimatedItem,
>(params: {
	previousItems?: T[] | null;
	nextItems: T[];
}): KeyedAnimationDelta<T> {
	const previousItems = params.previousItems ?? [];
	const { nextItems } = params;
	const previousItemMap = new Map(
		previousItems.map((item) => [item.key, item] as const),
	);
	const nextItemMap = new Map(
		nextItems.map((item) => [item.key, item] as const),
	);
	const sharedItems = nextItems.filter((item) =>
		previousItemMap.has(item.key),
	);
	const enteringItems = nextItems.filter(
		(item) => !previousItemMap.has(item.key),
	);
	const exitingItems = previousItems.filter(
		(item) => !nextItemMap.has(item.key),
	);

	return {
		sharedItems,
		enteringItems,
		exitingItems,
		animationRows: Math.max(
			resolveAnimatedRows(previousItems),
			resolveAnimatedRows(nextItems),
		),
		hasDelta: enteringItems.length > 0 || exitingItems.length > 0,
	};
}

export function resolveKeyedAnimationSequence<
	T extends KeyedAnimatedItem,
>(params: {
	previousItems?: T[] | null;
	nextItems: T[];
}): KeyedAnimationSequence<T> {
	const delta = resolveKeyedAnimationDelta(params);

	if (!delta.hasDelta) {
		return {
			delta,
			initialFrame: {
				phase: "stable",
				stableItems: delta.sharedItems,
				animatedItems: [],
			},
			followUpFrame: null,
			settledItems: params.nextItems,
		};
	}

	if (delta.exitingItems.length > 0) {
		return {
			delta,
			initialFrame: {
				phase: "exit",
				stableItems: delta.sharedItems,
				animatedItems: delta.exitingItems,
			},
			followUpFrame:
				delta.enteringItems.length > 0
					? {
							phase: "enter",
							stableItems: delta.sharedItems,
							animatedItems: delta.enteringItems,
						}
					: null,
			settledItems: params.nextItems,
		};
	}

	return {
		delta,
		initialFrame: {
			phase: "enter",
			stableItems: delta.sharedItems,
			animatedItems: delta.enteringItems,
		},
		followUpFrame: null,
		settledItems: params.nextItems,
	};
}
